import React, { useCallback, useState, useEffect, useMemo } from 'react';
import FilterListIcon from '@material-ui/icons/FilterList';
import { useHistory } from 'react-router-dom';
import { Modal } from '../../../components/Modal/Modal.component';
import { SelectableTable } from '../../../components/SelectableTable/SelectableTable.component';
import DropdownMenuFilter from '../../../components/DropdownMenu/DropdownMenuFilter.component';
import { Prompt, SubText } from '../../../components/Prompt/Prompt.component';
import ExaminerAssignNumber from '../../Examiner/ExaminerAssignNumber/ExaminerAssignNumber.component';
import {
  ForceSelectionCallback,
  IndexedStrings,
  SelectableTableColumn,
  TableSelection,
} from '../../../components/SelectableTable/SelectableTable.types';
import { Examiner, SelectedExaminer } from '../../../services/examiners/examiners.types';
import { ExaminerType } from '../ExaminerTreemap.types';
import { AssignExaminer, AssignSubmitData } from './AssignExaminerDialog.types';
import { getExaminersApi } from '../../../services/examiners/examiners.service';
import {
  assignAssessmentsGroupMember,
  AssignDomesticCoordinatorKk,
  assignExaminingTeamChairman,
  AssignExaminingTeamMemberAPI,
  assignExamTaskGroupCoordinator,
} from '../../../services/exams/examiningTeam/examiningTeam.service';
import { useStyles } from './AssignExaminerDialog.styles';
import Examiners from '../../../models/examiners.types';
import { defaultPageInfo, IPageInfo } from '../../../models/page.types';
import {
  ValidationResult,
  verifyTaskGroupCoordinator,
  verifyTeamChairmanCoordinator,
  verifyCZWOexaminer,
  verifyZEexaminer,
} from '../../../services/validation/validation.service';
import { AxiosError } from 'axios';
import { ButtonOne } from '../../../components/ButtonOne/ButtonOne.component';
import { getErrorPageUrl } from '../../../utils/routerUrls';
import {
  getCommitteesList,
  getExamCommitteeNameFromIdString,
} from '../../SampleAssessmentRepo/Helpers/AccountTypeRendering';
import { UserPermission } from '../../../enum/userPermissions';
import usePermission from '../../../hooks/usePermission';
import DotWithLabel from '../../../components/DotWithLabel.component';
import { useTranslation } from 'react-i18next';
import { useLayoutContext } from '../../../Context/Layout.context';

interface AssignExaminerDialogProps {
  id: string;
  title: string;
  type: ExaminerType;
  isOpen: boolean;
  onClose: () => void;
  examinerFunctionId: string;
  domesticCoordinatorId?: string;
  secondaryId?: string;
  isMultiChoice?: boolean;
  assessmentId?: string;
}

export const AssignExaminerDialog: React.FC<AssignExaminerDialogProps> = ({
  id,
  title,
  type,
  onClose,
  isOpen = false,
  examinerFunctionId,
  secondaryId,
  domesticCoordinatorId,
  isMultiChoice = false,
  assessmentId,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [filter, setFilter] = useState<IndexedStrings>({
    firstName: '',
    lastName: '',
    email: '',
    pesel: '',
    phone: '',
    examCommitteeShortName: '',
    isOnline: '',
  });
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);
  const [pageInfo, setPageInfo] = useState<IPageInfo>(defaultPageInfo);
  const [examiners, setExaminers] = useState<Examiner[]>([]);
  const [itemsSelection, setItemsSelection] = useState<TableSelection>({ items: [] });
  const [onForceSelectionCallback, setOnForceSelectionCallback] = useState<ForceSelectionCallback | undefined>(
    undefined,
  );
  const [selectedExaminers, setSelectedExaminers] = useState<SelectedExaminer[]>([]);
  const { setIsLoader } = useLayoutContext();

  const history = useHistory();

  const forceSelectionRef = useCallback(
    (forceSelectionCallback) => setOnForceSelectionCallback(() => forceSelectionCallback),
    [],
  );

  const handleFilterChange = useCallback(
    (name: string, value: string | string[]) => {
      typeof value === 'string' && value.length === 1 ? setIsValidFilter(false) : setIsValidFilter(true);
      setFilter((state) => {
        if (state[name] !== value) {
          return {
            ...state,
            [name]: value,
          };
        } else {
          return state;
        }
      });
    },
    [setFilter, filter],
  );

  const columns: SelectableTableColumn[] = [
    {
      id: 'userId',
      type: isMultiChoice ? 'checkbox' : 'radio',
      title: '',
      filterType: null,
    },
    {
      id: 'firstName',
      type: 'text',
      title: 'Imię',
      filterType: 'search',
    },
    {
      id: 'lastName',
      type: 'text',
      title: 'Nazwisko',
      filterType: 'search',
    },
    {
      id: 'email',
      type: 'text',
      title: 'E-mail',
      filterType: 'search',
    },
    {
      id: 'pesel',
      type: 'text',
      title: 'PESEL',
      filterType: 'search',
    },
    {
      id: 'examCommitteeShortName',
      type: 'text',
      title: 'Komisja',
      filterType: 'filter',
    },
    {
      id: 'phone',
      type: 'text',
      title: 'Numer telefonu',
      filterType: 'search',
    },
    {
      id: 'isOnline',
      type: 'text',
      title: 'Status konta',
      filterType: 'filter',
    },
  ];

  const handleCurrentPage = (response: IPageInfo) => {
    const { totalPages, pageSize, totalCount, hasPrevious, hasNext } = response;
    if (response.totalCount < pageInfo.pageSize) {
      setPageInfo({ currentPage: 0, totalPages, pageSize, totalCount, hasPrevious, hasNext });
    } else {
      setPageInfo(response);
    }
  };

  const disableChangesTillResponse = () => {
    setPageInfo((state) => ({
      ...state,
      hasNext: false,
      hasPrevious: false,
    }));
  };

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;
    if (isOpen) {
      const filters = Object.keys(filter).map((key) => {
        if (Array.isArray(filter[key])) {
          return (filter[key] as string[]).join(',');
        }
        return filter[key];
      });
      if (!isValidFilter) return;
      setIsLoader(true);
      disableChangesTillResponse();
      getExaminersApi(
        signal ?? undefined,
        pageInfo.pageSize,
        pageInfo.currentPage,
        undefined,
        undefined,
        ...(filters as string[]),
      )
        .then(
          ({ data }) => {
            setExaminers(
              data.examinersList.map((examiner) => ({
                ...examiner,
                isOnline: (
                  <DotWithLabel
                    color={examiner.isOnline ? '#347F42' : '#BC0000'}
                    label={examiner.isOnline ? 'online' : 'offline'}
                  />
                ),
                examCommitteeShortName: getExamCommitteeNameFromIdString(examiner.examCommitteId),
                id: examiner.userId,
              })),
            );
            handleCurrentPage(data.page);
          },
          (error: AxiosError) => {
            if (error?.message !== 'canceled') history.push(getErrorPageUrl(error?.code ?? 500, 'admin'));
          },
        )
        .finally(() => {
          setIsLoader(false);
        });
    }
    return () => abortController.abort();
  }, [isOpen, filter, pageInfo.currentPage, pageInfo.pageSize]);

  const [anchorElExamCommittee, setAnchorElExamCommittee] = useState<SVGSVGElement | HTMLElement | null>(null);

  const handleClickExamCommitteeMenu = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if (!anchorElExamCommittee) {
      setAnchorElExamCommittee(event.currentTarget);
    } else setAnchorElExamCommittee(null);
  };

  const [examCommitteeFilter, setExamCommitteeFilter] = useState<string[]>([]);

  const handleExamCommitteeFilterChange = useCallback(
    (value?: string) => {
      if (value && typeof value === 'string') {
        const val = examCommitteeFilter.some((f) => f === value)
          ? examCommitteeFilter.filter((f) => f !== value)
          : [...examCommitteeFilter, value];
        setExamCommitteeFilter(val);
        handleFilterChange('examCommitteeShortName', val);
      } else {
        setExamCommitteeFilter([]);
        handleFilterChange('examCommitteeShortName', []);
      }
    },
    [examCommitteeFilter],
  );

  const [anchorElIsOnline, setAnchorElIsOnline] = useState<SVGSVGElement | HTMLElement | null>(null);

  const handleClickIsOnlineMenu = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if (!anchorElIsOnline) {
      setAnchorElIsOnline(event.currentTarget);
    } else setAnchorElIsOnline(null);
  };

  const [isOnlineFilter, setIsOnlineFilter] = useState<string[]>([]);

  const handleIsOnlineFilterChange = useCallback(
    (value?: string) => {
      if (value && typeof value === 'string') {
        const val = isOnlineFilter.some((f) => f === value)
          ? isOnlineFilter.filter((f) => f !== value)
          : [...isOnlineFilter, value];
        setIsOnlineFilter(val);
        handleFilterChange('isOnline', val.length > 1 ? '' : (val[0] === 'Online').toString());
      } else {
        setIsOnlineFilter([]);
        handleFilterChange('isOnline', '');
      }
    },
    [isOnlineFilter],
  );

  const getUsers = useCallback(
    (_page?: number, _newPageSize?: number) => {
      setPageInfo((state) => ({
        ...state,
        currentPage: _page ? _page : 0,
        pageSize: _newPageSize ? _newPageSize : state.pageSize,
      }));
    },
    [filter, pageInfo],
  );

  const [assignNumberModalOpen, setAssignNumberModalOpen] = useState<boolean>(false);
  const [isSuccessPromptVisible, setIsSuccessPromptVisible] = useState<boolean>(false);
  const [successPromptMainText, setSuccessPromptMainText] = useState<string>('');
  const [successPromptText, setSuccessPromptText] = useState<SubText[]>([]);
  const [isConfirmationPromptVisible, setIsConfirmationPromptVisible] = useState<boolean>(false);
  const [confirmationPromptMainText, setConfirmationPromptMainText] = useState<string>('');
  const [confirmationPromptText, setConfirmationPromptText] = useState<SubText[]>([]);
  const [isWarningPromptVisible, setIsWarningPromptVisible] = useState<boolean>(false);
  const [warningPromptMainText, setWarningPromptMainText] = useState<string>('');
  const [warningPromptText, setWarningPromptText] = useState<SubText[]>([]);
  const [warningPromptSecondaryText, setWarningPromptSecondaryText] = useState<SubText[]>([]);

  const [isErrorPromptVisible, setIsErrorPromptVisible] = useState<boolean>(false);
  const [errorPromptMainText, setErrorPromptMainText] = useState<string>('');
  const [errorPromptText, setErrorPromptText] = useState<SubText[]>([]);

  const handleClose = useCallback(() => {
    setFilter({
      firstName: '',
      lastName: '',
      email: '',
      pesel: '',
      phone: '',
      examCommitteeShortName: '',
      isOnline: '',
    });
    setExaminers([]);
    setExamCommitteeFilter([]);
    setPageInfo(defaultPageInfo);
    onForceSelectionCallback && onForceSelectionCallback({ items: [] });
    setSelectedExaminers([]);
    onClose();
  }, [onClose]);

  const showSuccessPrompt = useCallback(
    (examiners: SelectedExaminer[], text: string) => {
      setIsErrorPromptVisible(false);
      setIsConfirmationPromptVisible(false);
      setIsSuccessPromptVisible(true);
      setIsWarningPromptVisible(false);
      setSuccessPromptMainText(text);
      setSuccessPromptText([
        {
          text: isMultiChoice
            ? examiners.map((e) => `${e.firstName} ${e.lastName}`).join(', ')
            : `${examiners[0].firstName} ${examiners[0].lastName}`,
        },
        {
          text: 'zakończone sukcesem.',
          isBold: true,
        },
      ]);
    },
    [isMultiChoice],
  );

  const showConfirmationPrompt = (examiners: SelectedExaminer[], title: string, text?: string) => {
    setIsErrorPromptVisible(false);
    setIsConfirmationPromptVisible(true);
    setIsSuccessPromptVisible(false);
    setIsWarningPromptVisible(false);
    setConfirmationPromptMainText(title);
    setAssignNumberModalOpen(false);
    setConfirmationPromptText([
      {
        text: examiners.map((e) => `${e.firstName} ${e.lastName}`).join(', '),
      },
      {
        text: text || 'nie powiodło się, spróbuj ponownie później.',
        isBold: true,
      },
    ]);
    onForceSelectionCallback && onForceSelectionCallback({ items: [] });
  };

  const showWarningPrompt = useCallback(
    (assignedExaminers: SelectedExaminer[], unassignedExaminers: SelectedExaminer[], text: string) => {
      setIsWarningPromptVisible(true);
      setIsConfirmationPromptVisible(false);
      setIsSuccessPromptVisible(false);
      setIsErrorPromptVisible(false);
      setWarningPromptMainText(text);
      setWarningPromptText([
        {
          text: isMultiChoice
            ? assignedExaminers.map((e) => `${e.firstName} ${e.lastName}`).join(', ')
            : `${examiners[0].firstName} ${examiners[0].lastName}`,
        },
        {
          text: 'zakończone sukcesem',
          isBold: true,
        },
      ]);
      setWarningPromptSecondaryText([
        {
          text: isMultiChoice
            ? unassignedExaminers.map((e) => `${e.firstName} ${e.lastName}`).join(', ')
            : `${examiners[0].firstName} ${examiners[0].lastName}`,
        },
        {
          text: 'zakończone niepowodzeniem',
          isBold: true,
        },
      ]);
    },
    [isMultiChoice],
  );

  const showErrorPrompt = useCallback(
    (examiners: SelectedExaminer[], title: string, text?: string, examinatorLabel?: boolean) => {
      setIsConfirmationPromptVisible(false);
      setIsErrorPromptVisible(true);
      setIsWarningPromptVisible(false);
      setIsSuccessPromptVisible(false);
      setErrorPromptMainText(title);
      setAssignNumberModalOpen(false);
      setErrorPromptText([
        {
          text: examinatorLabel ? 'Egzaminator:' : '',
          isBold: true,
        },
        {
          text: isMultiChoice
            ? examiners.map((e) => `${e.firstName} ${e.lastName}`).join(', ')
            : `${examiners[0].firstName} ${examiners[0].lastName}`,
        },
        {
          text: text || 'nie powiodło się, spróbuj ponownie później.',
          isBold: true,
        },
      ]);
      onForceSelectionCallback && onForceSelectionCallback({ items: [] });
    },
    [isMultiChoice],
  );

  const handleDomesticCoordinatorSubmit = (data: AssignSubmitData) => {
    if (data) {
      const { domesticCoordinatorId, examiners } = data;
      if (domesticCoordinatorId) {
        AssignDomesticCoordinatorKk(domesticCoordinatorId, examiners[0].userId)
          .then(({ data: result }) => {
            const { id, verificationResult } = result;
            if (!id || (id && verificationResult !== ValidationResult.Valid)) {
              switch (verificationResult) {
                case ValidationResult.InvalidAlreadyCoordinator:
                case ValidationResult.InvalidAlreadyExaminer:
                case ValidationResult.InvalidAlreadyChairman:
                  showErrorPrompt(
                    examiners,
                    'Przypisanie Egzaminatorowi funkcji Koordynatora Krajowego nie powiodło się.',
                    'pełni już funkcję KGZ/PZE/E w danej sesji oceniania.',
                  );
                  break;
                case ValidationResult.InvalidAlreadyDomesticCoordinator:
                  showErrorPrompt(
                    examiners,
                    'Przypisanie Egzaminatorowi funkcji Koordynatora Krajowego nie powiodło się.',
                    'pełni już funkcję Koordynatora Krajowego w danej sesji oceniania.',
                  );
                  break;
                default:
                  showErrorPrompt(examiners, 'Przypisanie funkcji Koordynatora Krajowego Egzaminatorowi:');
                  break;
              }
            } else {
              showSuccessPrompt(examiners, 'Przypisanie funkcji Koordynatora Krajowego Egzaminatorowi:');
            }
          })
          .catch(() => {
            showErrorPrompt(examiners, 'Przypisanie funkcji Koordynatora Krajowego Egzaminatorowi:');
          });
      }
    }
  };

  const handleExaminerKGZConfirm = (data: AssignSubmitData) => {
    const { examinerFunctionId, examiners, examinerNumber } = data;

    assignExamTaskGroupCoordinator(
      examinerFunctionId,
      examiners[0].userId,
      examinerNumber ? examinerNumber[0] : '',
    ).then(
      () => {
        showSuccessPrompt(examiners, 'Przypisanie funkcji KGZ Egzaminatorowi:');
      },
      () => {
        showErrorPrompt(examiners, 'Przypisanie Egzaminatorowi funkcji KGZ:');
      },
    );
  };

  const handleExaminerKGZSubmit = useCallback(
    (data: AssignSubmitData) => {
      if (data) {
        const { examinerFunctionId, examiners } = data;
        verifyTaskGroupCoordinator(examinerFunctionId, examiners[0].userId, assessmentId)
          .then(({ data: { verificationResult, examinerNumber } }) => {
            switch (verificationResult) {
              case ValidationResult.InvalidAlreadyCoordinator:
              case ValidationResult.InvalidAlreadyExaminer:
              case ValidationResult.InvalidAlreadyChairman:
                showErrorPrompt(
                  examiners,
                  'Przypisanie Egzaminatorowi funkcji KGZ nie powiodło się.',
                  'pełni już funkcję KGZ/PZE/E w danej sesji oceniania.',
                );
                break;
              case ValidationResult.InvalidAlreadyDomesticCoordinator:
                showErrorPrompt(
                  examiners,
                  'Przypisanie Egzaminatorowi funkcji KGZ nie powiodło się.',
                  'pełni już funkcję Koordynatora Krajowego w danej sesji oceniania.',
                );
                break;
              default:
                if (!examinerNumber) {
                  setAssignNumberModalOpen(true);
                } else {
                  handleExaminerKGZConfirm({ ...data, examinerNumber: [examinerNumber] });
                }
                break;
            }
          })
          .catch(() => {
            showErrorPrompt(examiners, 'Przypisanie Egzaminatorowi funkcji KGZ:');
          });
      }
    },
    [assessmentId],
  );

  const handleExaminerPZEConfirm = (data: AssignSubmitData) => {
    const { examinerFunctionId, examiners, examinerNumber } = data;
    assignExaminingTeamChairman(examinerFunctionId, examiners[0].userId, examinerNumber ? examinerNumber[0] : '').then(
      () => {
        showSuccessPrompt(examiners, 'Przypisanie funkcji PZE Egzaminatorowi:');
      },
      () => {
        showErrorPrompt(examiners, 'Przypisanie funkcji PZE Egzaminatorowi:');
      },
    );
  };

  const handleExaminerPZESubmit = useCallback(
    (data: AssignSubmitData) => {
      if (data) {
        const { examinerFunctionId, examiners } = data;
        verifyTeamChairmanCoordinator(examinerFunctionId, examiners[0].userId, assessmentId)
          .then(({ data: { verificationResult, examinerNumber } }) => {
            switch (verificationResult) {
              case ValidationResult.InvalidAlreadyCoordinator:
              case ValidationResult.InvalidAlreadyExaminer:
              case ValidationResult.InvalidAlreadyChairman:
                showErrorPrompt(
                  examiners,
                  'Przypisanie Egzaminatorowi funkcji PZE nie powiodło się.',
                  'pełni już funkcję KGZ/PZE/E w danej sesji oceniania.',
                );
                break;
              case ValidationResult.InvalidAlreadyDomesticCoordinator:
                showErrorPrompt(
                  examiners,
                  'Przypisanie Egzaminatorowi funkcji PZE nie powiodło się.',
                  'pełni już funkcję Koordynatora Krajowego w danej sesji oceniania.',
                );
                break;
              default:
                if (!examinerNumber) {
                  setAssignNumberModalOpen(true);
                } else {
                  handleExaminerPZEConfirm({ ...data, examinerNumber: [examinerNumber] });
                }
                break;
            }
          })
          .catch(() => {
            showErrorPrompt(examiners, 'Przypisanie Egzaminatorowi funkcji PZE: ');
          });
      }
    },
    [assessmentId],
  );

  const handleExaminerCZWOConfirm = (data: AssignSubmitData) => {
    const { examinerFunctionId, examiners } = data;
    const examinerAssigned: Examiners[] = examiners.map((value) => {
      return {
        examinerId: value.userId,
        examinerNumber: value.examinerNumber ?? '',
      };
    });
    assignAssessmentsGroupMember(examinerFunctionId, examinerAssigned)
      .then(() => {
        showSuccessPrompt(examiners, 'Dodanie do Zespołu Wzorcowych Ocen Egzaminatora:');
      })
      .catch(() => {
        showErrorPrompt(examiners, 'Dodanie do Zespołu Wzorcowych Ocen Egzaminatora: ');
      });
  };

  const handleExaminerCZWOSubmit = useCallback(
    (data: AssignSubmitData) => {
      if (data) {
        const { examinerFunctionId, examiners } = data;
        const usersId = examiners.map((value) => value.userId);
        verifyCZWOexaminer(examinerFunctionId, usersId, assessmentId)
          .then(({ data: result }) => {
            const verifications = result.filter((r) => r.verificationResult !== ValidationResult.Valid);
            const validExaminersIds: string[] = [];
            const invalidExaminers: AssignExaminer[] = [];
            examiners.forEach((item) => {
              if (verifications.find(({ id }) => id === item.userId)) {
                invalidExaminers.push(item);
              } else {
                validExaminersIds.push(item.userId);
              }
            });
            if (verifications.length) {
              switch (verifications[0].verificationResult) {
                case ValidationResult.InvalidAlreadyCoordinator:
                case ValidationResult.InvalidAlreadyExaminer:
                case ValidationResult.InvalidAlreadyChairman:
                  if (validExaminersIds.length !== 0) {
                    onForceSelectionCallback && onForceSelectionCallback({ items: validExaminersIds });
                    setAssignNumberModalOpen(true);

                    showConfirmationPrompt(
                      invalidExaminers,
                      `Dodanie niektórych z wybranych ${
                        invalidExaminers.length > 1 ? 'Egzaminatorów' : 'Egzaminatora'
                      } do Zespołu Wzorcowych Ocen nie powiodło się.`,
                      `${
                        invalidExaminers.length > 1 ? 'pełnią' : 'pełni'
                      } już funkcję KGZ/PZE/E w danej sesji oceniania.`,
                    );
                  } else {
                    showErrorPrompt(
                      invalidExaminers,
                      `Dodanie ${
                        invalidExaminers.length > 1 ? 'Egzaminatorów' : 'Egzaminatora'
                      } do Zespołu Wzorcowych Ocen nie powiodło się.`,
                      `${
                        invalidExaminers.length > 1 ? 'pełnią' : 'pełni'
                      } już funkcję KGZ/PZE/E w danej sesji oceniania.`,
                    );
                  }

                  break;
                case ValidationResult.InvalidAlreadyDomesticCoordinator:
                  showErrorPrompt(
                    invalidExaminers,
                    `Dodanie do Zespołu Wzorcowych Ocen ${
                      invalidExaminers.length > 1 ? 'Egzaminatorów' : 'Egzaminatora'
                    }:`,
                    `${
                      invalidExaminers.length > 1 ? 'pełnią' : 'pełni'
                    } już funkcję Koordynatora Krajowego w danej sesji oceniania.`,
                  );
                  break;
                case ValidationResult.InvalidAlreadyMemberOrChairman:
                  showErrorPrompt(
                    invalidExaminers,
                    'Dodanie Egzaminatora do Zespołu Wzorcowych Ocen nie powiodło się.',
                    'pełni już funkcję członka/przewodniczącego ZWO w danej sesji oceniania w danej grupie zadań.',
                    true,
                  );
                  break;
                default:
                  break;
              }
            } else {
              if (result.some((v) => !v.examinerNumber)) {
                onForceSelectionCallback && onForceSelectionCallback({ items: validExaminersIds });
                setAssignNumberModalOpen(true);
              } else {
                handleExaminerCZWOConfirm({ ...data, examinerNumber: result.map((v) => v.examinerNumber ?? '') });
              }
            }
          })
          .catch(() => {
            showErrorPrompt(examiners, 'Dodanie do Zespołu Egzaminatorów Egzamiantora:');
          });
      }
    },
    [assessmentId],
  );

  const handleExaminerZESubmit = useCallback(
    (data: AssignSubmitData) => {
      if (data) {
        const { secondaryId, examiners } = data;
        const usersId = examiners.map((value) => value.userId);
        verifyZEexaminer(secondaryId, usersId, assessmentId)
          .then(({ data: result }) => {
            const verifications = result.filter(
              (r) =>
                r.verificationResult !== ValidationResult.Valid &&
                r.verificationResult !== ValidationResult.InvalidAlreadyDomesticCoordinator,
            );
            const verificationExaminerAlreadyDomesticCoordinator = result.filter(
              (r) => r.verificationResult === ValidationResult.InvalidAlreadyDomesticCoordinator,
            );
            const validExaminersIds: string[] = [];
            const invalidExaminers: AssignExaminer[] = [];
            const invalidAlreadyDomesticCoordinators: AssignExaminer[] = [];

            examiners.forEach((item) => {
              if (verifications.find(({ id }) => id === item.userId)) {
                invalidExaminers.push(item);
              } else {
                validExaminersIds.push(item.userId);
              }
            });

            examiners.forEach((item) => {
              if (verificationExaminerAlreadyDomesticCoordinator.find(({ id }) => id === item.userId)) {
                invalidAlreadyDomesticCoordinators.push(item);
              }
            });

            if (verifications.length) {
              switch (verifications[0].verificationResult) {
                case ValidationResult.InvalidAlreadyCoordinator:
                case ValidationResult.InvalidAlreadyExaminer:
                case ValidationResult.InvalidAlreadyChairman:
                  showErrorPrompt(
                    invalidExaminers,
                    'Dodanie Egzaminatora do Zespołu Egzaminatorów nie powiodło się.',
                    'pełni już funkcję KGZ/PZE/E w danej sesji oceniania.',
                  );
                  break;
                default:
                  setAssignNumberModalOpen(true);
                  break;
              }
            } else if (verificationExaminerAlreadyDomesticCoordinator.length) {
              showErrorPrompt(
                invalidAlreadyDomesticCoordinators,
                'Dodanie do Zespołu Egzaminatorów Egzaminatora:',
                'pełni już funkcję Koordynatora Krajowego w danej sesji oceniania.',
              );
            } else {
              onForceSelectionCallback && onForceSelectionCallback({ items: validExaminersIds });
              setAssignNumberModalOpen(true);
            }
          })
          .catch(() => {
            showErrorPrompt(examiners, 'Dodanie do Zespołu Egzaminatorów Egzaminatora:');
          });
      }
    },
    [assessmentId],
  );

  const handleExaminerZEConfirm = (data: AssignSubmitData) => {
    const { secondaryId, examiners, examinerNumber } = data;

    const examinerAssigned: Examiners[] = examiners.map((value, index) => {
      return {
        examinerId: value.userId,
        examinerNumber: examinerNumber ? examinerNumber[index] : '',
        firstName: value.firstName,
        lastName: value.lastName,
      };
    });

    AssignExaminingTeamMemberAPI(secondaryId, examinerAssigned)
      .then((result) => {
        const unassignedExaminersIds = result.data
          .filter((res: Record<string, unknown>) => res.verificationResult !== 0)
          .map((item: Examiner) => item.id);

        const assignedExaminers = [] as SelectedExaminer[],
          unassignedExaminers = [] as SelectedExaminer[];
        examiners.forEach((item) => {
          if (!unassignedExaminersIds.includes(item.userId)) {
            assignedExaminers.push(item);
          } else {
            unassignedExaminers.push(item);
          }
        });

        if (unassignedExaminers.length === 0) {
          showSuccessPrompt(examiners, 'Dodanie do Zespołu Egzaminatorów Egzaminatora:');
        } else if (assignedExaminers.length === 0) {
          showErrorPrompt(examiners, 'Dodanie Egzaminatora do Zespołu Egzaminatorów:');
        } else {
          showWarningPrompt(assignedExaminers, unassignedExaminers, 'Dodanie Egzaminatora do Zespołu Egzaminatorów');
        }
      })
      .catch(() => {
        showErrorPrompt(examiners, 'Dodanie Egzaminatora do Zespołu Egzaminatorów:');
      });
  };

  const handleSubmit = useCallback(() => {
    if (type && itemsSelection.items.length) {
      const secId = secondaryId ?? '';
      const data: AssignSubmitData = {
        domesticCoordinatorId,
        secondaryId: secId,
        examinerFunctionId,
        examiners: examiners
          .filter((item) => itemsSelection.items.includes(item.userId))
          .map((item) => {
            return {
              firstName: item.firstName,
              lastName: item.lastName,
              userId: item.userId,
            };
          }),
      };
      switch (type) {
        case 'KK':
          handleDomesticCoordinatorSubmit(data);
          break;
        case 'KGZ':
          handleExaminerKGZSubmit(data);
          break;
        case 'PZE':
          handleExaminerPZESubmit(data);
          break;
        case 'ZE':
          handleExaminerZESubmit(data);
          break;
        case 'ZWO':
          handleExaminerCZWOSubmit(data);
          break;
        default:
          break;
      }
    }
  }, [type, examinerFunctionId, itemsSelection, secondaryId]);

  const handleConfirm = useCallback(
    (examinersNumbers: SelectedExaminer[]) => {
      if (type) {
        const secId = secondaryId ?? '';
        const examinerNumber = selectedExaminers.map((examiner) => {
          const ex = examinersNumbers.find((e) => e.userId === examiner.userId);
          if (ex) {
            examiner.examinerNumber = ex.examinerNumber;
          }
          return examiner;
        });
        const data: AssignSubmitData = {
          examinerFunctionId,
          secondaryId: secId,
          domesticCoordinatorId,
          examiners: selectedExaminers,
          examinerNumber: examinerNumber.map((examiner) => examiner.examinerNumber) as string[],
        };
        const multiData = {
          examinerFunctionId,
          secondaryId: secId,
          domesticCoordinatorId,
          examiners: examinerNumber,
        };
        switch (type) {
          case 'KGZ':
            handleExaminerKGZConfirm(data);
            break;
          case 'PZE':
            handleExaminerPZEConfirm(data);
            break;
          case 'ZE':
            handleExaminerZEConfirm(data);
            break;
          case 'ZWO':
            handleExaminerCZWOConfirm(multiData);
            break;
          default:
            break;
        }
      }
    },
    [type, examinerFunctionId, examiners, itemsSelection, secondaryId],
  );

  const filterElements: { [key: string]: JSX.Element } = {
    examCommitteeShortName: (
      <>
        <FilterListIcon
          onClick={handleClickExamCommitteeMenu}
          aria-controls="ExamCommittee-menu"
          aria-haspopup="true"
          style={{ color: examCommitteeFilter.length ? '#3560FF' : 'gray', paddingTop: '6px' }}
        />
        <DropdownMenuFilter
          anchorEl={anchorElExamCommittee}
          setAnchorEl={setAnchorElExamCommittee}
          menuItems={getCommitteesList()}
          hasCheckbox={true}
          checkedItems={examCommitteeFilter}
          onChange={handleExamCommitteeFilterChange}
          uncheckAllMenuItems={handleExamCommitteeFilterChange}
        />
      </>
    ),
    isOnline: (
      <>
        <FilterListIcon
          onClick={handleClickIsOnlineMenu}
          aria-controls="IsOnline-menu"
          aria-haspopup="true"
          style={{ color: isOnlineFilter.length ? '#3560FF' : 'gray', paddingTop: '6px' }}
        />
        <DropdownMenuFilter
          anchorEl={anchorElIsOnline}
          setAnchorEl={setAnchorElIsOnline}
          menuItems={['Online', 'Offline']}
          hasCheckbox={true}
          checkedItems={isOnlineFilter}
          onChange={handleIsOnlineFilterChange}
          uncheckAllMenuItems={handleIsOnlineFilterChange}
        />
      </>
    ),
  };

  const handleCloseSuccessModals = useCallback(() => {
    setIsSuccessPromptVisible(false);
    setAssignNumberModalOpen(false);
    handleClose();
  }, [handleClose]);

  const handleCloseErrorModals = useCallback(() => {
    setIsErrorPromptVisible(false);
  }, [handleClose]);

  const handleCloseWarningModals = useCallback(() => {
    setIsWarningPromptVisible(false);
    setAssignNumberModalOpen(false);
    handleClose();
  }, [handleClose]);

  const handleCloseConfirmationModals = useCallback(() => {
    setIsConfirmationPromptVisible(false);
  }, [handleClose]);

  const handleSelectionChange = useCallback(
    (itemsSelection: TableSelection) => {
      if (!examiners.length) return;
      setItemsSelection(itemsSelection);
      const previousSelectedExaminers = [...selectedExaminers];
      const newSelectedExaminers = examiners
        .filter((item) => itemsSelection.items.includes(item.userId))
        .map((item) => {
          return {
            userId: item.userId,
            firstName: item.firstName,
            lastName: item.lastName,
          };
        });
      const filteredNewSelectedExaminers = newSelectedExaminers.filter(
        (obj2) => !previousSelectedExaminers.some((obj1) => obj1.userId === obj2.userId),
      );
      const currentSelectedExaminers = [...previousSelectedExaminers, ...filteredNewSelectedExaminers];
      setSelectedExaminers(currentSelectedExaminers);
    },
    [examiners],
  );

  return (
    <>
      <Modal
        id={id}
        open={isOpen}
        size={'xl'}
        xCloseButton={handleClose}
        autoHeight={true}
        actions={true}
        title={title}
      >
        <div className={styles.table}>
          <SelectableTable<Examiner>
            columns={columns}
            rows={examiners}
            onFilterChange={handleFilterChange}
            filter={filter}
            filterElements={filterElements}
            pageInfo={pageInfo}
            getPage={getUsers}
            onSelectionChanged={handleSelectionChange}
            forceSelectionRef={forceSelectionRef}
            isMultiChoice={isMultiChoice}
            caption={title}
          />
        </div>
        <div className={styles.actions}>
          <ButtonOne
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={itemsSelection.items.length === 0}
          >
            wybierz
          </ButtonOne>
        </div>
        <Prompt
          type="error"
          isBold={true}
          open={isErrorPromptVisible}
          mainText={errorPromptMainText}
          subTexts={errorPromptText}
          mainActionButtonText="POWRÓT DO WYSZUKIWANIA"
          onConfirm={handleCloseErrorModals}
        />
        <Prompt
          type="confirmation"
          isBold={true}
          open={isConfirmationPromptVisible}
          mainText={confirmationPromptMainText}
          subTexts={confirmationPromptText}
          mainActionButtonText="KONTYNUUJ"
          onConfirm={handleCloseConfirmationModals}
        />
        <Prompt
          type="success"
          isBold={true}
          open={isSuccessPromptVisible}
          mainText={successPromptMainText}
          subTexts={successPromptText}
          mainActionButtonText="POWRÓT DO LISTY"
          onConfirm={handleCloseSuccessModals}
        />
        <Prompt
          type="warning"
          isBold={true}
          open={isWarningPromptVisible}
          mainText={warningPromptMainText}
          subTexts={warningPromptText}
          subTexts2={warningPromptSecondaryText}
          mainActionButtonText="POWRÓT DO LISTY"
          onConfirm={handleCloseWarningModals}
        />
      </Modal>
      <ExaminerAssignNumber
        open={assignNumberModalOpen}
        isBold={true}
        closeDialogs={() => setAssignNumberModalOpen(false)}
        confirm={handleConfirm}
        cancel={() => setAssignNumberModalOpen(false)}
        examiners={selectedExaminers.filter((examiner) => !examiner.examinerNumber)}
      />
    </>
  );
};
